'use client'

import { Icon } from "@/components/ui/icon"
import { useIsClient } from "@/hooks/use-is-client"
import { useAppDispatch, useAppSelector } from "@/lib/store"
import { selectCartItems } from "@/lib/store/selectors"
import { openCartDrawer } from "@/lib/store/features/cart/cartSlice"
import { Button, ButtonProps } from "@heroui/react"
import { useRouter } from "next/navigation"
import { useCallback } from "react"

export function HeaderCart(props: ButtonProps) {
  const cartItems = useAppSelector(selectCartItems)
  const dispatch = useAppDispatch()
  const router = useRouter()

  const handleCartClick = useCallback(() => {
    if (!cartItems.length) {
      router.push('/products')
      return
    }
    dispatch(openCartDrawer())
  }, [cartItems, router, dispatch])

  // The badge is the one piece of this header that depends on persisted state.
  // redux-persist rehydrates the cart from localStorage on the client only, so
  // the server always renders an empty cart. Gating the badge on `useIsClient`
  // keeps the first client render identical to the server HTML and lets the
  // badge appear on the pass after hydration. Without this the whole site
  // threw React #418 (hydration mismatch) on every page load whenever the
  // cart was non-empty, because the server had no badge node and the client
  // rendered one. Do NOT fix this by wrapping the app in PersistGate again:
  // that is what used to blank the entire server-rendered body.
  const isClient = useIsClient()

  const itemCount = cartItems.length

  return (
    <>
      <div className="relative w-10 h-10">
        <Button
          onPress={handleCartClick}
          aria-label="Shopping cart"
          {...props}
        >
          <Icon icon="cart" className="w-5 h-5" />
        </Button>
        {isClient && itemCount > 0 && (
          <div
            className="absolute -top-1 -right-1 flex items-center justify-center rounded-full bg-success text-[9px] text-white font-medium z-10 min-w-[16px] h-[16px] px-0.5 animate-[badge-pop_200ms_ease-out] motion-reduce:animate-none"
            key={itemCount}
          >
            {itemCount}
          </div>
        )}
      </div>
    </>
  )
}
